import type { AdminClass } from "../types/class.types";
import type { CatalogLevel1, CatalogLevel2 } from "../types/level.types";
import { buildLevelSeedState } from "../data/level.seed";
import { SEED_ADMIN_CLASSES } from "../data/class.mock";
import { loadMigratedClassStore } from "../utils/classStorage";
import type {
  Level1Value,
  Level2Value,
  Level3Value,
  LevelCatalogState,
  LevelListQuery,
  LevelTableRow,
  LevelTier,
  PaginatedLevelRows,
} from "../types/level.types";
import type { LevelApi } from "./levelApi.interface";
import { LEVELS_PER_PAGE } from "../constants/class.constants";
import { assertValidLabelText } from "@/utils/zodString";

const LEVEL_STORAGE_KEY = "mt_admin_levels_v1";
const VALUE_MAX_LENGTH = 150;

const delay = (ms = 300) => new Promise((r) => setTimeout(r, ms));

const uid = () => Math.random().toString(36).slice(2, 10);

function slugify(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80) || `level-${uid()}`;
}

function loadStore(): LevelCatalogState {
  try {
    const raw = localStorage.getItem(LEVEL_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as LevelCatalogState;
      if (parsed?.level1?.length) return parsed;
    }
  } catch {
    /* seed */
  }
  return buildLevelSeedState();
}

function saveStore(state: LevelCatalogState) {
  localStorage.setItem(LEVEL_STORAGE_KEY, JSON.stringify(state));
}

let memoryStore: LevelCatalogState | null = null;

function getStore(): LevelCatalogState {
  if (!memoryStore) {
    memoryStore = loadStore();
  }
  return memoryStore;
}

function persist(state: LevelCatalogState) {
  memoryStore = state;
  saveStore(state);
}

function loadClasses(): AdminClass[] {
  return loadMigratedClassStore(SEED_ADMIN_CLASSES);
}

function toCatalogShape(state: LevelCatalogState): CatalogLevel1[] {
  return state.level1
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((l1) => ({
      id: l1.id,
      label: l1.label,
      levels: state.level2
        .filter((l2) => l2.parentLevel1Id === l1.id)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(
          (l2): CatalogLevel2 => ({
            id: l2.id,
            label: l2.label,
            categorySlug: l2.categorySlug,
          })
        ),
    }));
}

function normalizeLabel(label: string) {
  return label.trim();
}

function duplicateExists(
  tier: LevelTier,
  label: string,
  state: LevelCatalogState,
  excludeId?: string,
  parentLevel1Id?: string
) {
  const norm = normalizeLabel(label).toLowerCase();
  if (tier === 1) {
    return state.level1.some(
      (v) => v.id !== excludeId && v.label.toLowerCase() === norm
    );
  }
  if (tier === 2) {
    return state.level2.some(
      (v) =>
        v.id !== excludeId &&
        v.parentLevel1Id === parentLevel1Id &&
        v.label.toLowerCase() === norm
    );
  }
  return state.level3.some(
    (v) => v.id !== excludeId && v.label.toLowerCase() === norm
  );
}

function paginateRows(
  rows: LevelTableRow[],
  query: LevelListQuery = {}
): PaginatedLevelRows {
  const pageSize = query.pageSize ?? LEVELS_PER_PAGE;

  let ordered = rows;
  if (query.sortBy) {
    const dir = query.sortOrder === "desc" ? -1 : 1;
    const key = query.sortBy;
    ordered = [...rows].sort((a, b) => {
      const av = (a as unknown as Record<string, unknown>)[key];
      const bv = (b as unknown as Record<string, unknown>)[key];
      if (av == null && bv == null) return 0;
      if (av == null) return -dir;
      if (bv == null) return dir;
      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
  }

  const totalItems = ordered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(query.page ?? 1, totalPages);
  const start = (currentPage - 1) * pageSize;

  return {
    data: ordered.slice(start, start + pageSize),
    totalItems,
    totalPages,
    currentPage,
  };
}

export const levelApiMock: LevelApi = {
  async getCatalogLevels() {
    await delay(0);
    return toCatalogShape(getStore());
  },

  async getAllLevel2Options() {
    await delay(0);
    const state = getStore();
    return state.level2
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((v) => ({ id: v.id, label: v.label }));
  },

  async getLevel3Options() {
    await delay(0);
    return getStore()
      .level3.slice()
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((v) => ({ id: v.id, label: v.label }));
  },

  async getLevelRows(tier, query = {}) {
    await delay(150);
    const state = getStore();
    const parentLevel1Id = query.parentLevel1Id;

    if (tier === 1) {
      const rows = state.level1
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((v) => ({ id: v.id, label: v.label, sortOrder: v.sortOrder }));
      return paginateRows(rows, query);
    }
    if (tier === 2) {
      const level2 = parentLevel1Id
        ? state.level2.filter((v) => v.parentLevel1Id === parentLevel1Id)
        : state.level2;
      const rows = level2
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((v) => ({
          id: v.id,
          label: v.label,
          sortOrder: v.sortOrder,
          parentLabel: state.level1.find((p) => p.id === v.parentLevel1Id)?.label,
        }));
      return paginateRows(rows, query);
    }
    const rows = state.level3
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((v) => ({ id: v.id, label: v.label, sortOrder: v.sortOrder }));
    return paginateRows(rows, query);
  },

  async addLevelValue(tier, label, options) {
    await delay(300);
    const trimmed = assertValidLabelText(label);
    if (trimmed.length > VALUE_MAX_LENGTH) {
      throw new Error(`Value cannot exceed ${VALUE_MAX_LENGTH} characters`);
    }

    const state = getStore();

    if (tier === 2 && !options?.parentLevel1Id) {
      throw new Error("Select a Level 1 category first");
    }

    if (duplicateExists(tier, trimmed, state, undefined, options?.parentLevel1Id)) {
      throw new Error("This value already exists");
    }

    const next = { ...state };

    if (tier === 1) {
      const item: Level1Value = {
        id: `l1-${uid()}`,
        label: trimmed,
        sortOrder: next.level1.length + 1,
      };
      next.level1 = [...next.level1, item];
      persist(next);
      return {
        id: item.id,
        label: item.label,
        sortOrder: item.sortOrder,
        message: "",
      };
    }

    if (tier === 2) {
      const item: Level2Value = {
        id: `l2-${uid()}`,
        label: trimmed,
        parentLevel1Id: options!.parentLevel1Id!,
        categorySlug: options?.categorySlug ?? slugify(trimmed),
        sortOrder: next.level2.filter((v) => v.parentLevel1Id === options!.parentLevel1Id).length + 1,
      };
      next.level2 = [...next.level2, item];
      persist(next);
      return {
        id: item.id,
        label: item.label,
        sortOrder: item.sortOrder,
        parentLabel: next.level1.find((p) => p.id === item.parentLevel1Id)?.label,
        message: "",
      };
    }

    const item: Level3Value = {
      id: `l3-${uid()}`,
      label: trimmed,
      sortOrder: next.level3.length + 1,
    };
    next.level3 = [...next.level3, item];
    persist(next);
    return {
      id: item.id,
      label: item.label,
      sortOrder: item.sortOrder,
      message: "",
    };
  },

  async updateLevelValue(tier, id, label, options) {
    await delay(300);

    const state = getStore();
    const existing =
      tier === 1
        ? state.level1.find((v) => v.id === id)
        : tier === 2
          ? state.level2.find((v) => v.id === id)
          : state.level3.find((v) => v.id === id);

    if (!existing) throw new Error("Value not found");

    const trimmed = assertValidLabelText(label, {
      previousLabel: options?.previousLabel ?? existing.label,
    });

    const next = { ...state };

    if (tier === 1) {
      const item = next.level1.find((v) => v.id === id);
      if (!item) throw new Error("Value not found");
      if (duplicateExists(tier, trimmed, state, id)) {
        throw new Error("This value already exists");
      }
      next.level1 = next.level1.map((v) =>
        v.id === id ? { ...v, label: trimmed } : v
      );
      persist(next);
      return {
        id,
        label: trimmed,
        sortOrder: item.sortOrder,
        message: "",
      };
    }

    if (tier === 2) {
      const item = next.level2.find((v) => v.id === id);
      if (!item) throw new Error("Value not found");
      if (duplicateExists(tier, trimmed, state, id, item.parentLevel1Id)) {
        throw new Error("This value already exists");
      }
      next.level2 = next.level2.map((v) =>
        v.id === id ? { ...v, label: trimmed, categorySlug: slugify(trimmed) } : v
      );
      persist(next);
      return {
        id,
        label: trimmed,
        sortOrder: item.sortOrder,
        parentLabel: next.level1.find((p) => p.id === item.parentLevel1Id)?.label,
        message: "",
      };
    }

    const item = next.level3.find((v) => v.id === id);
    if (!item) throw new Error("Value not found");
    if (duplicateExists(tier, trimmed, state, id)) {
      throw new Error("This value already exists");
    }
    next.level3 = next.level3.map((v) =>
      v.id === id ? { ...v, label: trimmed } : v
    );
    persist(next);
    return {
      id,
      label: trimmed,
      sortOrder: item.sortOrder,
      message: "",
    };
  },

  async deleteLevelValue(tier, id) {
    await delay(300);
    const state = getStore();
    const row =
      tier === 1
        ? state.level1.find((v) => v.id === id)
        : tier === 2
          ? state.level2.find((v) => v.id === id)
          : state.level3.find((v) => v.id === id);

    if (!row) throw new Error("Value not found");

    const inUse = await this.isLevelValueInUse(tier, id, row.label);
    if (inUse) {
      throw new Error(
        "This level is used by one or more classes and cannot be deleted."
      );
    }

    const next = { ...state };
    if (tier === 1) {
      next.level1 = next.level1.filter((v) => v.id !== id);
      next.level2 = next.level2.filter((v) => v.parentLevel1Id !== id);
    } else if (tier === 2) {
      next.level2 = next.level2.filter((v) => v.id !== id);
    } else {
      next.level3 = next.level3.filter((v) => v.id !== id);
    }
    persist(next);
    return { message: "" };
  },

  async isLevelValueInUse(tier, id, label) {
    await delay(0);
    const classes = loadClasses();
    return classes.some((c) => {
      if (tier === 1) {
        return c.levels.level1Id === id;
      }
      if (tier === 2) {
        return c.levels.level2Id === id;
      }
      return c.levels.level3Id === id || c.levels.level3Label === label;
    });
  },
};
